import {
  APICallState,
  AuthActions,
  MFAActions,
  Selectors,
  AuthActions as WidgetsAuthActions,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library';
import React, {useEffect} from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';
import {useLocation} from 'react-router-dom';
import {ROUTES} from 'routes';

import OptInClose from 'components/OptInClose';
import OptinRequired from 'components/OptinRequired';
import PRCodeEntry from 'components/PasswordReset/PRCodeEntry';
import PRInformationEntry from 'components/PasswordReset/PRInformationEntry';
import PRSuccess from 'components/PasswordReset/PRSuccess';
import ConfirmationSuccess from 'containers/ConfirmationSuccess';
import ConnectedPRNewPasswordEntry from 'containers/ConnectedPRNewPasswordEntry';
import ConnectedOptoutDialogBox from 'containers/OptoutDialogBox';
import ConnectedPreferenceSelector from 'containers/PreferenceSelector';
import {useGetForwardUrl} from 'hooks/useGetFowardUrl';
import {useShouldContactHost} from 'hooks/useShouldContactHost';
import OptIn from 'routes/OptIn';
import SendCodeSuccess from 'routes/SendCodeSuccess';
import StepUpScreen from 'routes/StepUp';
import VIPHostInfoHotlineScreen from 'routes/VIPHostInfoHotline';
import VerifyIdentity from 'routes/VerifyIdentity';

import './App.css';

function App() {
  const forwardUrl = useGetForwardUrl() || '';
  const widgetDispatch = useWidgetDispatch();
  const isLoggedIn = useWidgetState(Selectors.isLoggedInSelector);
  const loadStatus = useWidgetState(Selectors.mfaProfileStatusSelector);
  const shouldContactHost = useShouldContactHost();

  useEffect(() => {
    widgetDispatch(WidgetsAuthActions.refreshUserSession());
  }, [widgetDispatch]);

  useEffect(() => {
    /**
     * if user is logged in but profile is not populated yet, make the request
     */
    if (isLoggedIn && loadStatus === APICallState.INITIAL) {
      widgetDispatch(MFAActions.getSecurityProfile());
      widgetDispatch(MFAActions.getOptinStatus());
    }
  }, [isLoggedIn, loadStatus, widgetDispatch]);

  useEffect(() => {
    /**
     * FIXME: should we organize startup items better?
     * This is a call to determine is the app is in web view
     */
    widgetDispatch(AuthActions.setIsWebView());
  }, [widgetDispatch]);

  const location = useLocation();
  const isPasswordReset =
    location?.pathname && location.pathname.startsWith('/password-reset/');

  if (!isLoggedIn && !isPasswordReset) {
    window.open(
      `/rewards/signin?forwardUrl=${encodeURIComponent(forwardUrl)}`,
      '_self',
    );
  }

  if (isLoggedIn) {
    return (
      <Routes>
        {/** TODO: When we have actual service, first check if user has previously opted out of MFA,
         *  If so, navigate to optInRequired instead of Home (only if they're coming from Marketplace?)
         */}
        <Route path={ROUTES.optInRequired} element={<OptinRequired />} />
        <Route path={ROUTES.hostInfo} element={<VIPHostInfoHotlineScreen />} />
        <Route path={ROUTES.home} element={<OptIn />} />
        <Route path={ROUTES.optInClose} element={<OptInClose />} />
        {/**
         * Temporary redirect from '/opt-in' to '/'
         * '/opt-in' will be removed.
         */}
        <Route
          path={ROUTES.optIn}
          element={
            <Navigate
              to={{
                pathname: ROUTES.home,
                search: window.location.search,
                hash: window.location.hash,
              }}
            />
          }
        />
        {shouldContactHost ? (
          <Route
            path="*"
            element={
              <Navigate
                to={{
                  pathname: ROUTES.hostInfo,
                  search: window.location.search,
                  hash: window.location.hash,
                }}
              />
            }
          />
        ) : (
          <>
            <Route
              path={ROUTES.preference}
              element={<ConnectedPreferenceSelector />}
            />
            <Route
              path={ROUTES.optOut}
              element={<ConnectedOptoutDialogBox />}
            />
            <Route path={ROUTES.verifyIdentity} element={<VerifyIdentity />} />
            <Route
              path={ROUTES.sendCodeSuccess}
              element={<SendCodeSuccess />}
            />
            <Route
              path={ROUTES.confirmationSuccess}
              element={
                <ConfirmationSuccess
                  autoNavigate={true}
                  forwardUrl={forwardUrl}
                />
              }
            />
            <Route path={ROUTES.stepUp} element={<StepUpScreen />} />
          </>
        )}
        {/**
         * Pasword Reset stuff
         *   */}
        <Route
          path={ROUTES.passwordResetInformation}
          element={<PRInformationEntry />}
        />
        <Route path={ROUTES.passwordResetCode} element={<PRCodeEntry />} />
        <Route
          path={ROUTES.passwordResetNewPassword}
          element={<ConnectedPRNewPasswordEntry />}
        />
        <Route path={ROUTES.passwordResetSuccess} element={<PRSuccess />} />
        <Route path={ROUTES.home} element={<PRInformationEntry />} />
      </Routes>
    );
  } else {
    return (
      <Routes>
        <Route
          path={ROUTES.passwordResetInformation}
          element={<PRInformationEntry />}
        />
        <Route path={ROUTES.passwordResetCode} element={<PRCodeEntry />} />
        <Route
          path={ROUTES.passwordResetNewPassword}
          element={<ConnectedPRNewPasswordEntry />}
        />
        <Route path={ROUTES.passwordResetSuccess} element={<PRSuccess />} />
        <Route path={ROUTES.home} element={<PRInformationEntry />} />
      </Routes>
    );
  }
}

export default App;
